import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, Check, Webhook, Shield, RefreshCw, Bell, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function DocsWebhookSetup() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const eventTypes = [
    { event: 'product.created', description: 'A new product is added to your catalog' },
    { event: 'product.updated', description: 'Product details (price, title, inventory) are modified' },
    { event: 'product.deleted', description: 'A product is removed from the catalog' },
    { event: 'order.created', description: 'A new order is placed' },
    { event: 'order.paid', description: 'Payment is confirmed for an order' },
    { event: 'order.fulfilled', description: 'An order has been shipped or fulfilled' },
    { event: 'inventory.low', description: 'Stock falls below the configured threshold' },
    { event: 'deal.created', description: 'An AI agent creates a new deal' },
    { event: 'deal.expired', description: 'A deal reaches its expiration date' },
    { event: 'agent.decision', description: 'An agent makes a decision requiring notification' },
    { event: 'health.score_change', description: 'Your CI Health Score changes significantly' },
  ];

  const payloadExample = `{
  "event": "product.updated",
  "timestamp": "2026-01-15T10:30:00Z",
  "data": {
    "product_id": "prod_abc123",
    "title": "Wireless Bluetooth Headphones",
    "price": 79.99,
    "previous_price": 99.99,
    "inventory": 42,
    "changes": ["price", "inventory"]
  },
  "merchant_id": "merch_xyz789",
  "webhook_id": "wh_evt_def456"
}`;

  const registerApiExample = `// Register a webhook via API
const response = await fetch('https://api.commerceindex.ai/v1/webhooks', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-CI-Api-Key': 'your_api_key',
    'X-CI-Merchant-Id': 'merch_xyz789'
  },
  body: JSON.stringify({
    url: 'https://your-server.com/webhooks/ci',
    events: [
      'product.updated',
      'order.created',
      'deal.created',
      'health.score_change'
    ],
    secret: 'whsec_your_webhook_secret'
  })
});

const webhook = await response.json();
console.log('Webhook registered:', webhook.id);`;

  const nodeHandlerExample = `// Node.js webhook handler (Express)
const express = require('express');
const crypto = require('crypto');

const app = express();
app.use(express.raw({ type: 'application/json' }));

const WEBHOOK_SECRET = process.env.CI_WEBHOOK_SECRET;

function verifySignature(payload, signature) {
  const expected = crypto
    .createHmac('sha256', WEBHOOK_SECRET)
    .update(payload)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected)
  );
}

app.post('/webhooks/ci', (req, res) => {
  const signature = req.headers['x-ci-signature'];

  if (!verifySignature(req.body, signature)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  const event = JSON.parse(req.body);

  switch (event.event) {
    case 'product.updated':
      console.log('Product updated:', event.data.product_id);
      // Sync product changes to your system
      break;
    case 'order.created':
      console.log('New order:', event.data.order_id);
      break;
    case 'deal.created':
      console.log('New deal:', event.data.deal_id);
      break;
    case 'health.score_change':
      console.log('Health score changed:', event.data.new_score);
      break;
    default:
      console.log('Unhandled event:', event.event);
  }

  res.status(200).json({ received: true });
});

app.listen(3000, () => console.log('Webhook server running on port 3000'));`;

  const pythonHandlerExample = `# Python webhook handler (Flask)
import hmac
import hashlib
import json
from flask import Flask, request, jsonify

app = Flask(__name__)
WEBHOOK_SECRET = os.environ.get('CI_WEBHOOK_SECRET')

def verify_signature(payload, signature):
    expected = hmac.new(
        WEBHOOK_SECRET.encode('utf-8'),
        payload,
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, signature)

@app.route('/webhooks/ci', methods=['POST'])
def handle_webhook():
    signature = request.headers.get('X-CI-Signature')

    if not verify_signature(request.data, signature):
        return jsonify({'error': 'Invalid signature'}), 401

    event = request.get_json()

    if event['event'] == 'product.updated':
        print(f"Product updated: {event['data']['product_id']}")
        # Sync product changes
    elif event['event'] == 'order.created':
        print(f"New order: {event['data']['order_id']}")
    elif event['event'] == 'deal.created':
        print(f"New deal: {event['data']['deal_id']}")
    elif event['event'] == 'health.score_change':
        print(f"Health score: {event['data']['new_score']}")

    return jsonify({'received': True}), 200

if __name__ == '__main__':
    app.run(port=3000)`;

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Integration
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Webhook Setup
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Receive real-time notifications when events occur in your Commerce Index account.
          Configure webhooks to keep your systems in sync.
        </p>
      </div>

      {/* Registering Webhooks */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Webhook className="w-6 h-6 text-[#3EF1C7]" />
          Registering Webhooks
        </h2>
        <p className="text-[#94A3B8] mb-4">
          You can register webhook endpoints through the Commerce Index dashboard or via the API.
          In the dashboard, navigate to <strong className="text-[#E2E8F0]">Settings → Webhooks → Add Endpoint</strong> and
          enter your URL and select the events you want to receive.
        </p>
        <p className="text-[#94A3B8] mb-4">
          Alternatively, register programmatically via the API:
        </p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(registerApiExample, 'register')}
          >
            {copiedCode === 'register' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{registerApiExample}</code>
          </pre>
        </div>
      </section>

      {/* Event Types */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Bell className="w-6 h-6 text-[#3EF1C7]" />
          Event Types
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Subscribe to any combination of the following event types:
        </p>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Event</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Description</th>
                </tr>
              </thead>
              <tbody>
                {eventTypes.map((item, i) => (
                  <tr key={i} className="border-b border-[#22304A] last:border-0">
                    <td className="p-3">
                      <code className="text-[#3EF1C7] bg-[#3EF1C7]/10 px-2 py-1 rounded text-xs">{item.event}</code>
                    </td>
                    <td className="p-3 text-[#E2E8F0]">{item.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {/* Payload Format */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Payload Format
        </h2>
        <p className="text-[#94A3B8] mb-4">
          All webhook payloads follow a consistent JSON structure with the event type, timestamp, event data, and your merchant ID:
        </p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(payloadExample, 'payload')}
          >
            {copiedCode === 'payload' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{payloadExample}</code>
          </pre>
        </div>
      </section>

      {/* Signature Verification */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-[#3EF1C7]" />
          Signature Verification
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Every webhook request includes an <code className="text-[#3EF1C7]">X-CI-Signature</code> header
          containing an HMAC-SHA256 signature of the request body. Always verify this signature to ensure
          the webhook is authentic and hasn't been tampered with.
        </p>
        <Card className="bg-[#0D1628] border-[#22304A] mb-4">
          <CardContent className="pt-6">
            <ol className="space-y-2 text-[#E2E8F0] text-sm">
              <li><span className="text-[#3EF1C7] font-semibold">1.</span> Extract the <code className="text-[#3EF1C7]">X-CI-Signature</code> header from the request</li>
              <li><span className="text-[#3EF1C7] font-semibold">2.</span> Compute HMAC-SHA256 of the raw request body using your webhook secret</li>
              <li><span className="text-[#3EF1C7] font-semibold">3.</span> Compare the computed hash with the received signature using a timing-safe comparison</li>
              <li><span className="text-[#3EF1C7] font-semibold">4.</span> Reject the request if signatures don't match</li>
            </ol>
          </CardContent>
        </Card>
      </section>

      {/* Retry Policy */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <RefreshCw className="w-6 h-6 text-[#3EF1C7]" />
          Retry Policy
        </h2>
        <p className="text-[#94A3B8] mb-4">
          If your endpoint returns a non-2xx status code or times out, Commerce Index will retry delivery
          up to <strong className="text-[#E2E8F0]">3 times</strong> with exponential backoff:
        </p>
        <div className="grid grid-cols-3 gap-4 mb-4">
          {[
            { retry: 'Retry 1', delay: '1 second', description: 'Immediate retry for transient failures' },
            { retry: 'Retry 2', delay: '5 seconds', description: 'Short delay for temporary outages' },
            { retry: 'Retry 3', delay: '30 seconds', description: 'Final attempt before marking as failed' },
          ].map((item, i) => (
            <Card key={i} className="bg-[#0D1628] border-[#22304A]">
              <CardContent className="pt-6 text-center">
                <p className="text-[#3EF1C7] font-semibold text-lg">{item.delay}</p>
                <p className="text-[#E2E8F0] text-sm font-medium mt-1">{item.retry}</p>
                <p className="text-[#94A3B8] text-xs mt-2">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-[#94A3B8] text-sm">
          Your endpoint must respond within <strong className="text-[#E2E8F0]">10 seconds</strong>.
          After all retries are exhausted, the event is logged as failed and visible in your dashboard
          under <strong className="text-[#E2E8F0]">Webhooks → Failed Deliveries</strong>.
        </p>
      </section>

      {/* Node.js Handler */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Node.js Handler Example
        </h2>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(nodeHandlerExample, 'node')}
          >
            {copiedCode === 'node' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{nodeHandlerExample}</code>
          </pre>
        </div>
      </section>

      {/* Python Handler */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Python Handler Example
        </h2>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(pythonHandlerExample, 'python')}
          >
            {copiedCode === 'python' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{pythonHandlerExample}</code>
          </pre>
        </div>
      </section>

      {/* What's Next */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">What's Next?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/docs/api-reference">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer">
              <CardContent className="pt-6">
                <h3 className="text-[#E2E8F0] font-semibold mb-2 flex items-center gap-2">
                  API Reference <ArrowRight className="w-4 h-4 text-[#3EF1C7]" />
                </h3>
                <p className="text-[#94A3B8] text-sm">Full API documentation for all endpoints including webhook management.</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/guardrails">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer">
              <CardContent className="pt-6">
                <h3 className="text-[#E2E8F0] font-semibold mb-2 flex items-center gap-2">
                  Guardrails <ArrowRight className="w-4 h-4 text-[#3EF1C7]" />
                </h3>
                <p className="text-[#94A3B8] text-sm">Configure safety limits and approval rules for AI agent actions.</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
